import { Component, Input, OnDestroy, OnInit, inject, ChangeDetectionStrategy, model } from '@angular/core';

import { MessageService } from '../../services/message/message.service';
import { WorkflowService } from '../../services/workflow/workflow.service';
import { ToastrService } from 'ngx-toastr';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Notebook } from '../../interfaces/project.interface';
import { GeneralHelpers } from '../../helpers/general.helper';

import { Subscription } from 'rxjs';
import { DialogService } from '../../services/dialog/dialog.service';
import { NOTEBOOK_FORM_UPDATE, RELOAD_NOTEBOOKS, RELOAD_FILES, RELOAD_CHUNKS, RELOAD_GLOBAL_VAR, RELOAD_WASM } from '../../constants/general.constants';
import { AppStateService } from '../../services/app-state/app-state.service'
import { AutoUnsubscribe } from '../../decorators/auto-unsubscribe.decorator';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';

@AutoUnsubscribe()
@Component({
  standalone: true,
  selector: 'app-notebook-form',
  templateUrl: './notebook-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    // Material
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatProgressBarModule,
    // Form
    ReactiveFormsModule,
    FormsModule,
    // Layout
    FlexLayoutModule,
  ],
})
export class NotebookFormComponent implements OnInit, OnDestroy {
  private messageService = inject(MessageService);
  private notebookService = inject(WorkflowService);
  private toastr = inject(ToastrService);
  private _formBuilder = inject(FormBuilder);
  private dialogService = inject(DialogService);

  #appState = inject(AppStateService);

  workMode = model<'create' | 'update'>('create');

  @Input() formAlign: 'row' | "column" = 'row';

  @Input() notebook: Notebook | any;

  public notebookForm!: FormGroup;

  public messageSubscription!: Subscription;

  constructor() {
    this.messageSubscription = this.messageService
      .getMessage()
      .subscribe((message: any) => {
        if (message && (message.text === NOTEBOOK_FORM_UPDATE)) {
          this.updateForm(this.notebookService.getSelectedNotebook());
        }
      });
  }

  ngOnInit() {
    this.initForm();
    if (this.workMode() === 'update') {
      this.updateForm(this.notebookService.getSelectedNotebook());
    }
  }


  ngOnDestroy() {
    this.messageSubscription.unsubscribe();
  }
  // ─────────────────────────────────────────────────────────────────────
  // Notebook form

  private initForm() {
    this.notebookForm = this._formBuilder.group({
      name: ['', [Validators.required]],
    });
  }

  public processNotebookForm(formValue: any, isValid: boolean, $event: Event) {
    $event.preventDefault();
    if (!isValid) {
      return;
    }

    if (this.workMode() === 'create') {
      this.createNotebook(formValue);
    } else if (this.workMode() === 'update') {
      this.updateNotebook(formValue);
    }
  }

  private createNotebook(formValue: any) {
    this.notebookService.create(
      {
        name: formValue.name,
        chunkName: '',
        chunkTypeId: 1,
      },
      String(this.#appState.projectId()),
    ).subscribe({
      next: (data: Notebook | any) => {
        this.notebookCallback(data?.notebook);
      },
      error: () => {

      }
    });
  }

  private updateNotebook(formValue: any) {
    this.notebookService.update(
      {
        name: formValue.name,
        defaultState: this.notebookService.getSelectedNotebook().defaultState || ''
      },
      String(this.#appState.workflowId()),
      String(this.#appState.projectId()),
    ).subscribe({
      next: (data: Notebook | any) => {
        this.notebookCallback(data);
      },
      error: () => {

      }
    });
  }

  private updateForm(data: Notebook) {
    this.notebookForm.get('name')?.setValue(data.name);
    this.workMode.set('update');
  }

  private notebookCallback(data?: Notebook) {
    if (data) {
      this.notebookService.setWorkflowId(Number(data.workflowId));
    }

    this.messageService.sendMessage(RELOAD_NOTEBOOKS);
    this.messageService.sendMessage(RELOAD_FILES);
    this.messageService.sendMessage(RELOAD_CHUNKS);
    this.messageService.sendMessage(RELOAD_GLOBAL_VAR);
    this.messageService.sendMessage(RELOAD_WASM);

    this.toastr.success('Success', `Workflow ${this.workMode() === 'create' ? 'created' : 'updated'}`);

    GeneralHelpers.resetForm(this as any, 'notebookForm');

    this.workMode.set('create');
  }

  public cancel() {
    this.workMode.set('create');
    this.resetForm();
  }

  private resetForm() {
    this.notebookForm.reset();
    Object.values(this.notebookForm.controls).forEach((control: any) => {
      control.setErrors(null);
      control.markAsUntouched();
      control.markAsPristine();
    });
  }

  public openDeleteConfirmationPopup(id: string = this.#appState.workflowId().toString()) {
    this.dialogService
      .confirmDialog({
        title: 'Are you sure ?',
        message: 'After you delete workflow you will lost data forever',
        confirmCaption: 'Confirm',
        cancelCaption: 'Cancel',
        dialogData: {
          id
        },
      })
      .subscribe((data: any) => {
        if (data?.dialogData && data.dialogData.id) {

          this.notebookService
            .delete(this.#appState.workflowId().toString(), this.#appState.projectId().toString())
            .subscribe({
              next: () => {
                this.toastr.success("Workflow was deleted");

                this.notebookCallback();
              },
              error: () => {
                this.toastr.error("Workflow was not deleted");
              }
            })
        }
      });
  }
}
